import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles, ShieldCheck, Calendar, Plus, Trash2 } from "lucide-react";
import { previewEstimate, submitEstimate, getPricingSettings } from "@/lib/estimates.functions";
import { getMyProfile } from "@/lib/portal/portal.functions";
import { supabase } from "@/integrations/supabase/client";
import { HONEYPOT_FIELD } from "@/lib/spam-protection";
import { track } from "@/lib/analytics/track";
import {
  computeEstimate, DEFAULT_PRICING,
  PROJECT_TYPE_LABELS, SURFACE_LABELS, ROOM_TYPES,
  type EstimateResult, type ProjectType, type PaintQuality,
  type SurfaceCondition, type Surface, type RoomSpec, type PricingSettings,
} from "@/lib/estimates";

export type StudioAttachment = {
  studioProjectId?: string | null;
  studioVersionId?: string | null;
  designImageUrl?: string | null;
  selectedColors?: Array<{ id: string; name: string; number?: string; hex: string; surface?: string }>;
  favoriteColors?: Array<{ id: string; name: string; number?: string; hex: string }>;
  detectedSurfaces?: Surface[];
};

type Contact = { name: string; email: string; phone: string; address: string; city: string };

// Per-project-scope state. Multiple sections can be combined in one estimate.
type Section = {
  projectType: ProjectType;
  rooms: RoomSpec[];
  surfaces: Surface[];
  cabinetDoorDrawerCount: string;
  trimLinearFeet: string;
  doorCount: string;
  windowCount: string;
  interiorSqft: string;
  interiorCeilingHeight: string;
  fenceLinearFeet: string;
  deckSqft: string;
  exteriorSqft: string;
  garageSqft: string;
  commercialSqft: string;
  surfaceCondition: SurfaceCondition;
  repairsNeeded: string;
  numberOfCoats: 1 | 2 | 3;
  paintQuality: PaintQuality;
};

type State = {
  contact: Contact;
  sections: Section[];
  activeIdx: number;
  timeline: string;
  notes: string;
};
// Compat alias so the existing ScopeFields signature (`s: State`) keeps working —
// it only reads section fields.
type ScopeState = Section;

const blankRoom = (): RoomSpec => ({
  type: "Living Room", length: 14, width: 16, ceilingHeight: 9, doorCount: 1, windowCount: 2,
});

const blankSection = (): Section => ({
  projectType: "single_room",
  rooms: [blankRoom()],
  surfaces: ["walls"],
  cabinetDoorDrawerCount: "",
  trimLinearFeet: "",
  doorCount: "",
  windowCount: "",
  interiorSqft: "",
  interiorCeilingHeight: "9",
  fenceLinearFeet: "",
  deckSqft: "",
  exteriorSqft: "",
  garageSqft: "",
  commercialSqft: "",
  surfaceCondition: "good",
  repairsNeeded: "",
  numberOfCoats: 2,
  paintQuality: "premium",
});

const initial: State = {
  contact: { name: "", email: "", phone: "", address: "", city: "" },
  sections: [blankSection()],
  activeIdx: 0,
  timeline: "",
  notes: "",
};

const PROJECT_TYPES: ProjectType[] = [
  "interior","exterior","single_room","multiple_rooms","cabinets","trim","doors","fence","deck","garage","commercial",
];

const usesRooms = (t: ProjectType) => t === "single_room" || t === "multiple_rooms";

const DEFAULT_SURFACES: Record<ProjectType, Surface[]> = {
  interior: ["walls", "ceiling", "trim"],
  single_room: ["walls"],
  multiple_rooms: ["walls"],
  exterior: ["walls", "trim"],
  cabinets: ["cabinets"],
  trim: ["trim"],
  doors: ["doors"],
  fence: [],
  deck: [],
  garage: ["walls"],
  commercial: ["walls"],
};

const AVAILABLE_SURFACES: Record<ProjectType, Surface[]> = {
  interior: ["walls","ceiling","trim","baseboards","crown","doors","windows","cabinets"],
  single_room: ["walls","ceiling","trim","baseboards","crown","doors","windows"],
  multiple_rooms: ["walls","ceiling","trim","baseboards","crown","doors","windows"],
  exterior: ["walls","trim","doors"],
  cabinets: ["cabinets"],
  trim: ["trim","baseboards","crown"],
  doors: ["doors"],
  fence: [],
  deck: [],
  garage: ["walls","ceiling","trim"],
  commercial: ["walls","ceiling","trim","doors"],
};

export function EstimateWizard({ attachment }: { attachment?: StudioAttachment }) {
  // phase progression:
  //   "section"  : editing sections[activeIdx] through Project/Scope/Surfaces/Condition/Paint
  //   "review"   : list of completed sections + option to add another
  //   "contact"  : shared contact + notes + captcha
  //   "estimate" : result panel
  type Phase = "section" | "review" | "contact" | "estimate";
  const [phase, setPhase] = useState<Phase>("section");
  const [sectionStep, setSectionStep] = useState(0);
  const [s, setS] = useState<State>(initial);
  const [pricing, setPricing] = useState<PricingSettings>(DEFAULT_PRICING);
  const [result, setResult] = useState<EstimateResult | null>(null);
  const [sectionResults, setSectionResults] = useState<EstimateResult[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Turnstile removed from estimate flow
  const startedAtRef = useRef<number>(Date.now());
  const honeypotRef = useRef<HTMLInputElement>(null);

  const fetchPricing = useServerFn(getPricingSettings);
  const preview = useServerFn(previewEstimate);
  const submit = useServerFn(submitEstimate);

  useEffect(() => { fetchPricing().then(setPricing).catch(() => {}); }, [fetchPricing]);

  // Prefill contact from the signed-in user's profile, if available.
  const fetchProfile = useServerFn(getMyProfile);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || cancelled) return;
      try {
        const p = await fetchProfile();
        if (cancelled || !p) return;
        setS((prev) => {
          const c = prev.contact;
          return {
            ...prev,
            contact: {
              name: c.name || p.preferences?.display_name || "",
              email: c.email || p.email || "",
              phone: c.phone || p.preferences?.phone || "",
              address: c.address || p.property?.address || "",
              city: c.city || p.property?.city || "",
            },
          };
        });
      } catch { /* not signed in / no profile — ignore */ }
    })();
    return () => { cancelled = true; };
  }, [fetchProfile]);

  // Studio attachment hints: prefill surfaces on the first section.
  useEffect(() => {
    if (!attachment?.designImageUrl) return;
    setS((p) => {
      const sec = p.sections[0];
      const next: Section = {
        ...sec,
        projectType: "single_room",
        surfaces: attachment.detectedSurfaces?.length ? attachment.detectedSurfaces : sec.surfaces,
      };
      return { ...p, sections: p.sections.map((x, i) => i === 0 ? next : x) };
    });
  }, [attachment?.designImageUrl, attachment?.detectedSurfaces]);

  const active: Section = s.sections[s.activeIdx];
  const SECTION_STEPS = useMemo(() => buildSectionSteps(active.projectType), [active.projectType]);

  const updateActive = <K extends keyof Section>(key: K, v: Section[K]) => setS((p) => ({
    ...p,
    sections: p.sections.map((sec, i) => i === p.activeIdx ? { ...sec, [key]: v } : sec),
  }));
  const updateContact = <K extends keyof Contact>(key: K, v: string) =>
    setS((p) => ({ ...p, contact: { ...p.contact, [key]: v } }));
  const setProjectType = (t: ProjectType) => setS((p) => ({
    ...p,
    sections: p.sections.map((sec, i) => i === p.activeIdx ? ({
      ...sec, projectType: t,
      rooms: usesRooms(t) ? (sec.rooms.length ? sec.rooms : [blankRoom()]) : [],
      surfaces: DEFAULT_SURFACES[t],
    }) : sec),
  }));

  // For "Entire Interior" we synthesize whole-house rooms from totals.
  const synthInteriorRooms = (sec: Section): RoomSpec[] => {
    if (sec.projectType !== "interior") return sec.rooms;
    const sqft = numOrNull(sec.interiorSqft);
    const h = numOrNull(sec.interiorCeilingHeight) ?? 9;
    if (!sqft || sqft <= 0) return [];
    const roomCount = Math.max(1, Math.round(sqft / 200));
    const perRoom = sqft / roomCount;
    const totalDoors = numOrNull(sec.doorCount) ?? 0;
    const totalWindows = numOrNull(sec.windowCount) ?? 0;
    return Array.from({ length: roomCount }, (_, i) => ({
      type: "Whole home",
      floorSqft: perRoom,
      ceilingHeight: h,
      doorCount: i === 0 ? totalDoors : 0,
      windowCount: i === 0 ? totalWindows : 0,
    }));
  };

  const effectiveRooms = (sec: Section): RoomSpec[] => sec.projectType === "interior" ? synthInteriorRooms(sec) : sec.rooms;
  const toggleSurface = (sv: Surface) => updateActive("surfaces",
    active.surfaces.includes(sv) ? active.surfaces.filter((x) => x !== sv) : [...active.surfaces, sv],
  );
  const updateRoom = (i: number, patch: Partial<RoomSpec>) => updateActive("rooms",
    active.rooms.map((r, idx) => idx === i ? { ...r, ...patch } : r),
  );
  const addRoom = () => updateActive("rooms", [...active.rooms, blankRoom()]);
  const removeRoom = (i: number) => updateActive("rooms", active.rooms.filter((_, idx) => idx !== i));

  const sectionToInput = (sec: Section) => ({
    projectType: sec.projectType,
    rooms: effectiveRooms(sec),
    surfaces: sec.surfaces,
    cabinetDoorDrawerCount: numOrNull(sec.cabinetDoorDrawerCount),
    trimLinearFeet: numOrNull(sec.trimLinearFeet),
    doorCount: numOrNull(sec.doorCount),
    fenceLinearFeet: numOrNull(sec.fenceLinearFeet),
    deckSqft: numOrNull(sec.deckSqft),
    exteriorSqft: numOrNull(sec.exteriorSqft),
    garageSqft: numOrNull(sec.garageSqft),
    commercialSqft: numOrNull(sec.commercialSqft),
    surfaceCondition: sec.surfaceCondition,
    numberOfCoats: sec.numberOfCoats,
    paintQuality: sec.paintQuality,
  });

  const liveActive = useMemo(() => computeEstimate(sectionToInput(active), pricing), [active, pricing]);
  const liveTotal = useMemo(() => {
    const parts = s.sections.map((sec) => computeEstimate(sectionToInput(sec), pricing));
    return {
      low: parts.reduce((sum, r) => sum + r.priceLow, 0),
      high: parts.reduce((sum, r) => sum + r.priceHigh, 0),
    };
  }, [s.sections, pricing]);

  const canNext = () => {
    if (phase === "contact") {
      const c = s.contact;
      return !!(c.name.trim() && /.+@.+/.test(c.email) && c.phone.trim().length >= 7);
    }
    return true;
  };

  const buildPayload = () => {
    const first = s.sections[0];
    const firstInput = sectionToInput(first);
    return {
      name: s.contact.name.trim(),
      email: s.contact.email.trim(),
      phone: s.contact.phone.trim(),
      address: s.contact.address.trim() || null,
      city: s.contact.city.trim() || null,
      // Top-level (legacy) fields mirror the first section for backwards compat.
      projectType: first.projectType,
      rooms: firstInput.rooms,
      surfaces: first.surfaces,
      cabinetDoorDrawerCount: firstInput.cabinetDoorDrawerCount,
      trimLinearFeet: firstInput.trimLinearFeet,
      doorCount: firstInput.doorCount,
      fenceLinearFeet: firstInput.fenceLinearFeet,
      deckSqft: firstInput.deckSqft,
      exteriorSqft: firstInput.exteriorSqft,
      garageSqft: firstInput.garageSqft,
      commercialSqft: firstInput.commercialSqft,
      surfaceCondition: first.surfaceCondition,
      repairsNeeded: first.repairsNeeded || null,
      numberOfCoats: first.numberOfCoats,
      paintQuality: first.paintQuality,
      timeline: s.timeline || null,
      notes: s.notes || null,
      studioProjectId: attachment?.studioProjectId ?? null,
      studioVersionId: attachment?.studioVersionId ?? null,
      designImageUrl: attachment?.designImageUrl ?? null,
      selectedColors: attachment?.selectedColors ?? [],
      favoriteColors: attachment?.favoriteColors ?? [],
      sections: s.sections.map(sectionToInput),
    };
  };

  const goSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const computed = await preview({ data: payload as never });
      setResult(computed);
      const perSection = s.sections.map((sec) => computeEstimate(sectionToInput(sec), pricing));
      setSectionResults(perSection);
      const anon = typeof window !== "undefined" ? window.localStorage.getItem("mp_studio_anon") : null;
      const out = await submit({ data: {
        ...payload,
        anonToken: anon,
        honeypot: honeypotRef.current?.value ?? null,
        formStartedAt: startedAtRef.current,
        turnstileToken: null,
      } });
      setSubmittedId(out.id);
      track.funnel("direct_estimate", "estimate_submitted", 5, {
        project_type: s.sections.map((x) => x.projectType).join("+"),
        section_count: s.sections.length,
        from_studio: !!attachment?.designImageUrl,
      });
      if (attachment?.designImageUrl) {
        track.funnel("studio_to_estimate", "estimate_submitted", 6);
      }
      try {
        if (typeof window !== "undefined" && window.sessionStorage.getItem("mp_from_gallery") === "1") {
          track.funnel("gallery_to_lead", "estimate_submitted", 3);
          window.sessionStorage.removeItem("mp_from_gallery");
        }
      } catch {}
      track.event("estimate_submitted", { source: attachment?.designImageUrl ? "studio" : "wizard", sections: s.sections.length });
      setPhase("estimate");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = async () => {
    setError(null);
    if (phase === "contact") { await goSubmit(); return; }
    if (phase === "section") {
      if (sectionStep < SECTION_STEPS.length - 1) {
        setSectionStep((x) => x + 1);
        return;
      }
      // finished the last section step -> go to review
      setPhase("review");
      setSectionStep(0);
      return;
    }
    if (phase === "review") {
      setPhase("contact");
      return;
    }
  };

  const goBack = () => {
    setError(null);
    if (phase === "section" && sectionStep > 0) { setSectionStep((x) => x - 1); return; }
    if (phase === "section" && sectionStep === 0) {
      // Go back to review if there are other sections; otherwise nothing.
      if (s.sections.length > 1 || s.activeIdx > 0) setPhase("review");
      return;
    }
    if (phase === "review") {
      // Back to editing the last section from Paint step.
      const idx = s.sections.length - 1;
      setS((p) => ({ ...p, activeIdx: idx }));
      setPhase("section");
      const steps = buildSectionSteps(s.sections[idx].projectType);
      setSectionStep(steps.length - 1);
      return;
    }
    if (phase === "contact") { setPhase("review"); return; }
  };

  const addAnotherSection = () => {
    setS((p) => ({ ...p, sections: [...p.sections, blankSection()], activeIdx: p.sections.length }));
    setPhase("section");
    setSectionStep(0);
  };
  const editSection = (idx: number) => {
    setS((p) => ({ ...p, activeIdx: idx }));
    setPhase("section");
    setSectionStep(0);
  };
  const removeSection = (idx: number) => {
    setS((p) => {
      if (p.sections.length <= 1) return p;
      const sections = p.sections.filter((_, i) => i !== idx);
      const activeIdx = Math.min(p.activeIdx, sections.length - 1);
      return { ...p, sections, activeIdx };
    });
  };

  // Progress indicator segments across the whole flow.
  const PROGRESS = useMemo(() => {
    const before = s.sections.slice(0, s.activeIdx).map((sec, i) => `${PROJECT_TYPE_LABELS[sec.projectType]} ✓`);
    const current = SECTION_STEPS.map((label) => `${PROJECT_TYPE_LABELS[active.projectType]} · ${label}`);
    return [...before, ...current, "Review", "Contact", "Estimate"];
  }, [s.sections, s.activeIdx, SECTION_STEPS, active.projectType]);
  const progressIdx = phase === "section"
    ? s.activeIdx + sectionStep  // rough: prior sections + current step
    : phase === "review" ? PROGRESS.length - 3
    : phase === "contact" ? PROGRESS.length - 2
    : PROGRESS.length - 1;

  return (
    <div className="max-w-3xl mx-auto">
      <input ref={honeypotRef} type="text" name={HONEYPOT_FIELD} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" defaultValue="" />

      <div className="flex items-center gap-2 mb-8">
        {PROGRESS.map((label, i) => (
          <div key={i} className="flex-1">
            <div className={`h-1 rounded-full transition ${i <= progressIdx ? "bg-amber-300" : "bg-white/10"}`} />
          </div>
        ))}
      </div>

      <div className="border border-white/10 rounded-2xl bg-white/[0.02] p-6 md:p-8">
        {attachment?.designImageUrl && phase !== "estimate" && (
          <div className="flex items-center gap-3 mb-6 p-3 rounded-xl bg-amber-300/5 border border-amber-300/20">
            <img src={attachment.designImageUrl} alt="Your design" className="w-16 aspect-video object-cover rounded-md border border-white/10" />
            <div className="text-xs">
              <p className="text-amber-200 font-medium">Design from AI Studio attached</p>
              <p className="text-zinc-500">We pre-filled surfaces from your design. Just confirm room dimensions.</p>
            </div>
          </div>
        )}

        {phase === "section" && s.sections.length > 1 && (
          <p className="text-[10px] uppercase tracking-widest text-amber-200 mb-3">
            Project {s.activeIdx + 1} of {s.sections.length}
          </p>
        )}

        {phase === "section" && SECTION_STEPS[sectionStep] === "Project" && (
          <StepBlock title="What are we painting?" subtitle="Pick the closest match — we'll adapt the next questions.">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {PROJECT_TYPES.map((t) => (
                <Choice key={t} active={active.projectType === t} onClick={() => setProjectType(t)} label={PROJECT_TYPE_LABELS[t]} />
              ))}
            </div>
          </StepBlock>
        )}

        {phase === "section" && SECTION_STEPS[sectionStep] === "Rooms" && (
          <StepBlock title="Tell us about the room(s)" subtitle="Enter dimensions, or just approximate square footage. Either works.">
            <div className="space-y-4">
              {active.rooms.map((r, i) => (
                <RoomCard key={i} index={i} room={r} onChange={(p) => updateRoom(i, p)} onRemove={active.rooms.length > 1 ? () => removeRoom(i) : undefined} />
              ))}
            </div>
            {(active.projectType === "interior" || active.projectType === "multiple_rooms") && (
              <button onClick={addRoom} className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-200 hover:text-amber-100">
                <Plus className="size-3.5" /> Add another room
              </button>
            )}
          </StepBlock>
        )}

        {phase === "section" && SECTION_STEPS[sectionStep] === "Scope" && (
          <StepBlock title="Project scope" subtitle="Give us the size and we'll work out a real range.">
            <ScopeFields s={active} update={updateActive} toggleSurface={toggleSurface} />
          </StepBlock>
        )}

        {phase === "section" && SECTION_STEPS[sectionStep] === "Surfaces" && (
          <StepBlock title="Which surfaces are included?" subtitle="Estimate covers only what you select.">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {AVAILABLE_SURFACES[active.projectType].map((sv) => (
                <Toggle key={sv} active={active.surfaces.includes(sv)} onClick={() => toggleSurface(sv)} label={SURFACE_LABELS[sv]} />
              ))}
              {AVAILABLE_SURFACES[active.projectType].length === 0 && (
                <p className="col-span-full text-sm text-zinc-500">No additional surfaces — pricing is based on the measurements above.</p>
              )}
            </div>
            {active.surfaces.includes("cabinets") && (
              <div className="mt-5">
                <Field label="Cabinet doors + drawers (count)">
                  <input inputMode="numeric" value={active.cabinetDoorDrawerCount} onChange={(e) => updateActive("cabinetDoorDrawerCount", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 24" />
                </Field>
              </div>
            )}
          </StepBlock>
        )}

        {phase === "section" && SECTION_STEPS[sectionStep] === "Condition" && (
          <StepBlock title="Surface condition" subtitle="More prep means more labor — be honest, it keeps your estimate accurate.">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
              {(["excellent","good","fair","poor","heavy"] as const).map((c) => (
                <Choice key={c} active={active.surfaceCondition === c} onClick={() => updateActive("surfaceCondition", c)} label={c === "heavy" ? "Heavy repairs" : c[0].toUpperCase() + c.slice(1)} />
              ))}
            </div>
            <Field label="Repairs needed (drywall, holes, water damage, peeling, etc.)">
              <textarea value={active.repairsNeeded} onChange={(e) => updateActive("repairsNeeded", e.target.value)} className={inputCls + " min-h-20"} placeholder="Optional" />
            </Field>
          </StepBlock>
        )}

        {phase === "section" && SECTION_STEPS[sectionStep] === "Paint" && (
          <StepBlock title="Paint preferences" subtitle="Better paint covers better and lasts longer — but we'll always recommend what fits.">
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Coats</p>
            <div className="grid grid-cols-3 gap-2 mb-6">
              {[1,2,3].map((n) => (
                <Choice key={n} active={active.numberOfCoats === n} onClick={() => updateActive("numberOfCoats", n as 1|2|3)} label={`${n} coat${n>1?"s":""}`} />
              ))}
            </div>
            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Paint quality</p>
            <div className="grid sm:grid-cols-3 gap-2 mb-6">
              {(["builder","premium","luxury"] as const).map((q) => (
                <Choice key={q} active={active.paintQuality === q} onClick={() => updateActive("paintQuality", q)} label={q === "builder" ? "Builder Grade" : q[0].toUpperCase() + q.slice(1)} />
              ))}
            </div>
          </StepBlock>
        )}

        {phase === "review" && (
          <StepBlock title="Your projects so far" subtitle="Add another project to combine estimates, or continue to send.">
            <div className="space-y-3">
              {s.sections.map((sec, i) => {
                const r = computeEstimate(sectionToInput(sec), pricing);
                return (
                  <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-amber-200">Project {i + 1}</p>
                        <p className="text-white font-medium">{PROJECT_TYPE_LABELS[sec.projectType]}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => editSection(i)} className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10">Edit</button>
                        {s.sections.length > 1 && (
                          <button onClick={() => removeSection(i)} className="text-zinc-500 hover:text-red-400"><Trash2 className="size-4" /></button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-zinc-400">
                      ${r.priceLow.toLocaleString()} <span className="text-zinc-500">–</span> ${r.priceHigh.toLocaleString()}
                      <span className="text-zinc-500"> · {sec.surfaces.length} surface{sec.surfaces.length !== 1 ? "s" : ""} · {sec.paintQuality} paint</span>
                    </p>
                  </div>
                );
              })}
            </div>
            <button onClick={addAnotherSection} className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-amber-300/40 bg-amber-300/5 text-amber-200 hover:bg-amber-300/10 text-sm font-medium">
              <Plus className="size-4" /> Add another project (e.g. Exterior)
            </button>
            <div className="mt-6 rounded-xl bg-amber-300/5 border border-amber-300/20 p-4">
              <p className="text-[10px] uppercase tracking-widest text-amber-200 mb-1">Combined range</p>
              <p className="font-serif text-2xl text-white">${liveTotal.low.toLocaleString()} – ${liveTotal.high.toLocaleString()}</p>
            </div>
          </StepBlock>
        )}

        {phase === "contact" && (
          <StepBlock title="Where should we send the estimate?" subtitle="We'll follow up within one business day.">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name *"><input value={s.contact.name} onChange={(e) => updateContact("name", e.target.value)} className={inputCls} autoComplete="name" /></Field>
              <Field label="Email *"><input type="email" inputMode="email" value={s.contact.email} onChange={(e) => updateContact("email", e.target.value)} className={inputCls} autoComplete="email" /></Field>
              <Field label="Phone *"><input type="tel" inputMode="tel" value={s.contact.phone} onChange={(e) => updateContact("phone", e.target.value)} className={inputCls} autoComplete="tel" /></Field>
              <Field label="City"><input value={s.contact.city} onChange={(e) => updateContact("city", e.target.value)} className={inputCls} autoComplete="address-level2" /></Field>
              <div className="sm:col-span-2">
                <Field label="Property address"><input value={s.contact.address} onChange={(e) => updateContact("address", e.target.value)} className={inputCls} autoComplete="street-address" /></Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Desired timeline"><input value={s.timeline} onChange={(e) => setS((p) => ({ ...p, timeline: e.target.value }))} className={inputCls} placeholder="e.g. within 30 days" /></Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Anything else we should know"><textarea value={s.notes} onChange={(e) => setS((p) => ({ ...p, notes: e.target.value }))} className={inputCls + " min-h-24"} /></Field>
              </div>
            </div>
            <div className="mt-6 rounded-xl bg-amber-300/5 border border-amber-300/20 p-4">
              <p className="text-[10px] uppercase tracking-widest text-amber-200 mb-1">Combined range · {s.sections.length} project{s.sections.length > 1 ? "s" : ""}</p>
              <p className="font-serif text-2xl text-white">${liveTotal.low.toLocaleString()} – ${liveTotal.high.toLocaleString()}</p>
            </div>
          </StepBlock>
        )}

        {phase === "estimate" && result && (
          <ResultPanel result={result} submittedId={submittedId} attachment={attachment} sections={s.sections} sectionResults={sectionResults} />
        )}

        {error && <p className="mt-4 text-sm text-red-400">{error}</p>}

        {phase !== "estimate" && (
          <div className="mt-8 flex items-center justify-between">
            <button onClick={goBack} disabled={submitting || (phase === "section" && sectionStep === 0 && s.sections.length === 1 && s.activeIdx === 0)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm text-zinc-400 hover:text-white disabled:opacity-30">
              <ArrowLeft className="size-4" /> Back
            </button>

            {phase === "section" && sectionStep > 0 && (
              <div className="hidden sm:block text-xs text-zinc-500">
                This project: <span className="text-amber-200 font-semibold">${liveActive.priceLow.toLocaleString()} – ${liveActive.priceHigh.toLocaleString()}</span>
              </div>
            )}

            <button onClick={goNext} disabled={!canNext() || submitting}
              className="inline-flex items-center gap-2 bg-amber-300 text-zinc-950 font-semibold px-6 py-3 rounded-full text-sm hover:bg-amber-200 disabled:opacity-40">
              {submitting ? (<><Loader2 className="size-4 animate-spin" /> Building your estimate…</>)
                : phase === "contact" ? (<>See my estimate <Sparkles className="size-4" /></>)
                : phase === "review" ? (<>Continue to contact <ArrowRight className="size-4" /></>)
                : sectionStep === SECTION_STEPS.length - 1 ? (<>Review projects <ArrowRight className="size-4" /></>)
                : (<>Continue <ArrowRight className="size-4" /></>)}
            </button>
          </div>
        )}
      </div>

      <p className="mt-6 text-[11px] text-zinc-400 text-center leading-relaxed">
        <ShieldCheck className="inline size-3 mr-1 text-amber-300/70" />
        This is a <strong className="text-zinc-400">preliminary budget estimate only</strong>. Final pricing will be confirmed during the on-site estimate.
      </p>
    </div>
  );
}

function buildSectionSteps(t: ProjectType): string[] {
  const s = ["Project"];
  if (usesRooms(t)) s.push("Rooms");
  else s.push("Scope");
  s.push("Surfaces", "Condition", "Paint");
  return s;
}


function numOrNull(v: string): number | null {
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3.5 py-3 text-[16px] text-white placeholder:text-zinc-400 focus:border-amber-300/50 focus:outline-none transition";

function StepBlock({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-serif text-2xl md:text-3xl mb-2 text-white">{title}</h2>
      <p className="text-sm text-zinc-500 mb-6">{subtitle}</p>
      {children}
    </div>
  );
}

function Choice({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button onClick={onClick} className={`py-3 px-3 rounded-xl border text-sm font-medium transition ${active ? "border-amber-300/60 bg-amber-300/10 text-white" : "border-white/10 bg-white/[0.02] text-zinc-400 hover:text-white hover:border-white/20"}`}>{label}</button>
  );
}

function Toggle({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button onClick={onClick} className={`flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition ${active ? "border-amber-300/60 bg-amber-300/5 text-white" : "border-white/10 bg-white/[0.02] text-zinc-400"}`}>
      <span>{label}</span>
      <span className={`size-4 rounded border grid place-items-center ${active ? "bg-amber-300 border-amber-300 text-zinc-950" : "border-white/30"}`}>{active && <Check className="size-3" />}</span>
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-zinc-500 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function YesNo({ label, value, onChange }: { label: string; value: boolean; onChange: () => void }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">{label}</p>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => { if (!value) onChange(); }} className={`py-2 rounded-lg border text-sm transition ${value ? "border-amber-300/60 bg-amber-300/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}>Yes</button>
        <button type="button" onClick={() => { if (value) onChange(); }} className={`py-2 rounded-lg border text-sm transition ${!value ? "border-amber-300/60 bg-amber-300/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`}>No</button>
      </div>
    </div>
  );
}

function RoomCard({ index, room, onChange, onRemove }: {
  index: number; room: RoomSpec; onChange: (p: Partial<RoomSpec>) => void; onRemove?: () => void;
}) {
  const [mode, setMode] = useState<"dims" | "sqft">(room.floorSqft ? "sqft" : "dims");
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-widest text-amber-200">Room {index + 1}</p>
        {onRemove && <button onClick={onRemove} className="text-zinc-500 hover:text-red-400"><Trash2 className="size-4" /></button>}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <Field label="Room type">
          <select value={room.type ?? ""} onChange={(e) => onChange({ type: e.target.value })} className={inputCls}>
            {ROOM_TYPES.map((r) => <option key={r} value={r} className="bg-zinc-900">{r}</option>)}
          </select>
        </Field>
        <Field label="Ceiling height (ft)">
          <input inputMode="decimal" value={room.ceilingHeight ?? ""} onChange={(e) => onChange({ ceilingHeight: e.target.value ? Number(e.target.value) : null })} className={inputCls} placeholder="9" />
        </Field>
      </div>
      <div className="flex gap-2 mb-3 text-[11px]">
        <button onClick={() => setMode("dims")} className={`px-3 py-1 rounded-full border ${mode === "dims" ? "border-amber-300/60 text-amber-200" : "border-white/10 text-zinc-500"}`}>Length × Width</button>
        <button onClick={() => setMode("sqft")} className={`px-3 py-1 rounded-full border ${mode === "sqft" ? "border-amber-300/60 text-amber-200" : "border-white/10 text-zinc-500"}`}>Approx. sq ft</button>
      </div>
      {mode === "dims" ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Length (ft)">
            <input inputMode="decimal" value={room.length ?? ""} onChange={(e) => onChange({ length: e.target.value ? Number(e.target.value) : null, floorSqft: null })} className={inputCls} placeholder="14" />
          </Field>
          <Field label="Width (ft)">
            <input inputMode="decimal" value={room.width ?? ""} onChange={(e) => onChange({ width: e.target.value ? Number(e.target.value) : null, floorSqft: null })} className={inputCls} placeholder="16" />
          </Field>
        </div>
      ) : (
        <Field label="Floor area (sq ft)">
          <input inputMode="numeric" value={room.floorSqft ?? ""} onChange={(e) => onChange({ floorSqft: e.target.value ? Number(e.target.value) : null, length: null, width: null })} className={inputCls} placeholder="e.g. 220" />
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Field label="Doors in room">
          <input inputMode="numeric" value={room.doorCount ?? ""} onChange={(e) => onChange({ doorCount: e.target.value ? Number(e.target.value) : 0 })} className={inputCls} placeholder="1" />
        </Field>
        <Field label="Windows in room">
          <input inputMode="numeric" value={room.windowCount ?? ""} onChange={(e) => onChange({ windowCount: e.target.value ? Number(e.target.value) : 0 })} className={inputCls} placeholder="2" />
        </Field>
      </div>
    </div>
  );
}

function ScopeFields({ s, update, toggleSurface }: { s: ScopeState; update: <K extends keyof ScopeState>(k: K, v: ScopeState[K]) => void; toggleSurface: (sv: Surface) => void }) {
  const t = s.projectType;
  if (t === "interior") return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Total interior area to paint (sq ft)">
        <input inputMode="numeric" value={s.interiorSqft} onChange={(e) => update("interiorSqft", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 2200 (whole home living area)" />
      </Field>
      <Field label="Average ceiling height (ft)">
        <input inputMode="decimal" value={s.interiorCeilingHeight} onChange={(e) => update("interiorCeilingHeight", e.target.value)} className={inputCls} placeholder="9" />
      </Field>
      <Field label="Total interior doors">
        <input inputMode="numeric" value={s.doorCount} onChange={(e) => update("doorCount", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 12" />
      </Field>
      <Field label="Total windows">
        <input inputMode="numeric" value={s.windowCount} onChange={(e) => update("windowCount", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 15" />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Trim / baseboards (linear ft, optional)">
          <input inputMode="numeric" value={s.trimLinearFeet} onChange={(e) => update("trimLinearFeet", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 450 — leave blank to estimate from room perimeter" />
        </Field>
      </div>
      <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3">
        <YesNo label="Paint baseboards?" value={s.surfaces.includes("baseboards")} onChange={() => toggleSurface("baseboards")} />
        <YesNo label="Paint crown molding?" value={s.surfaces.includes("crown")} onChange={() => toggleSurface("crown")} />
      </div>
      <p className="sm:col-span-2 text-xs text-zinc-500">Not sure on counts? Give your best guess — we'll confirm everything during the free on-site walkthrough.</p>
    </div>
  );
  if (t === "cabinets") return (
    <Field label="Cabinet doors + drawers (total count)">
      <input inputMode="numeric" value={s.cabinetDoorDrawerCount} onChange={(e) => update("cabinetDoorDrawerCount", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 24" />
    </Field>
  );
  if (t === "trim") return (
    <Field label="Total trim (linear feet)">
      <input inputMode="numeric" value={s.trimLinearFeet} onChange={(e) => update("trimLinearFeet", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 120" />
    </Field>
  );
  if (t === "doors") return (
    <Field label="Number of doors">
      <input inputMode="numeric" value={s.doorCount} onChange={(e) => update("doorCount", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 8" />
    </Field>
  );
  if (t === "fence") return (
    <Field label="Fence length (linear feet)">
      <input inputMode="numeric" value={s.fenceLinearFeet} onChange={(e) => update("fenceLinearFeet", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 150" />
    </Field>
  );
  if (t === "deck") return (
    <Field label="Deck area (sq ft)">
      <input inputMode="numeric" value={s.deckSqft} onChange={(e) => update("deckSqft", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 300" />
    </Field>
  );
  if (t === "garage") return (
    <Field label="Garage floor (sq ft)">
      <input inputMode="numeric" value={s.garageSqft} onChange={(e) => update("garageSqft", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 480 (2-car)" />
    </Field>
  );
  if (t === "commercial") return (
    <Field label="Total space (sq ft)">
      <input inputMode="numeric" value={s.commercialSqft} onChange={(e) => update("commercialSqft", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 4500" />
    </Field>
  );
  if (t === "exterior") return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Exterior siding area (sq ft)">
        <input inputMode="numeric" value={s.exteriorSqft} onChange={(e) => update("exteriorSqft", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 2200" />
      </Field>
      <Field label="Trim (linear ft, optional)">
        <input inputMode="numeric" value={s.trimLinearFeet} onChange={(e) => update("trimLinearFeet", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 320" />
      </Field>
      <Field label="Exterior doors (optional)">
        <input inputMode="numeric" value={s.doorCount} onChange={(e) => update("doorCount", e.target.value.replace(/[^0-9]/g, ""))} className={inputCls} placeholder="e.g. 2" />
      </Field>
    </div>
  );
  return null;
}

function PreviewPanel({ result }: { result: EstimateResult }) {
  return (
    <div className="mt-6 rounded-xl bg-amber-300/5 border border-amber-300/20 p-4">
      <p className="text-[10px] uppercase tracking-widest text-amber-200 mb-2">Live preview · range only</p>
      <p className="font-serif text-2xl text-white">${result.priceLow.toLocaleString()} – ${result.priceHigh.toLocaleString()}</p>
      <p className="text-xs text-zinc-500 mt-1">≈ {result.gallons} gal · {result.laborHours} labor hrs · {result.durationDays} day{result.durationDays > 1 ? "s" : ""}</p>
    </div>
  );
}

function ResultPanel({ result, submittedId, attachment, sections, sectionResults }: {
  result: EstimateResult;
  submittedId: string | null;
  attachment?: StudioAttachment;
  sections: Section[];
  sectionResults: EstimateResult[];
}) {
  return (
    <div>
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-green-400/20 bg-green-400/10 text-[11px] font-medium text-green-300 mb-5">
        <Check className="size-3" /> Request received {submittedId && `· #${submittedId.slice(0, 8)}`}
      </div>
      <h2 className="font-serif text-3xl md:text-4xl text-white mb-2">Your estimated investment</h2>
      <p className="text-sm text-zinc-500 mb-8">A real estimator will follow up within one business day to schedule an on-site walkthrough and confirm pricing.</p>

      <div className="rounded-2xl bg-gradient-to-br from-amber-300/15 to-yellow-500/0 border border-amber-300/30 p-6 md:p-8 mb-6">
        <p className="text-[10px] uppercase tracking-widest text-amber-200 mb-3">
          Combined Investment · {sections.length} project{sections.length > 1 ? "s" : ""} · range only
        </p>
        <p className="font-serif text-5xl md:text-6xl text-white">${result.priceLow.toLocaleString()} <span className="text-amber-200/60">–</span> ${result.priceHigh.toLocaleString()}</p>
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10">
          <Stat label="Paint required" value={result.gallons ? `${result.gallons} gal` : "—"} />
          <Stat label="Estimated labor" value={`${result.laborHours} hrs`} />
          <Stat label="Estimated duration" value={`${result.durationDays} day${result.durationDays > 1 ? "s" : ""}`} />
        </div>
      </div>

      {sections.length > 1 && sectionResults.length === sections.length && (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-6">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Breakdown by project</p>
          <ul className="divide-y divide-white/5">
            {sections.map((sec, i) => (
              <li key={i} className="flex items-center justify-between py-2 text-sm">
                <span className="text-zinc-300">{PROJECT_TYPE_LABELS[sec.projectType]}</span>
                <span className="text-zinc-400 tabular-nums">${sectionResults[i].priceLow.toLocaleString()} – ${sectionResults[i].priceHigh.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.lineItems.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-6">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Line items</p>
          <ul className="divide-y divide-white/5">
            {result.lineItems.map((li) => (
              <li key={li.label} className="flex items-center justify-between py-2 text-sm">
                <span className="text-zinc-300">{li.label}{li.detail && <span className="text-zinc-400"> · {li.detail}</span>}</span>
                <span className="text-zinc-400 tabular-nums">${li.low.toLocaleString()} – ${li.high.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {attachment?.designImageUrl && (
        <div className="rounded-xl border border-white/10 p-4 mb-6 flex items-center gap-4">
          <img src={attachment.designImageUrl} alt="Attached design" className="w-24 aspect-video object-cover rounded-lg" />
          <div className="text-xs text-zinc-400">
            <p className="text-white font-medium mb-1">Design attached</p>
            <p>We'll prepare your quote with the colors you picked in the AI Studio.</p>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 mb-6">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Assumptions</p>
        <p className="text-xs text-zinc-400 leading-relaxed">{result.assumptions}</p>
      </div>

      <a href="tel:6088864598" className="w-full inline-flex items-center justify-center gap-2 bg-amber-300 text-zinc-950 font-semibold py-4 rounded-full text-sm hover:bg-amber-200 transition mb-3">
        <Calendar className="size-4" /> Schedule my on-site estimate · (608) 886-4598
      </a>
      <p className="text-[11px] text-zinc-400 text-center">Or reply to the confirmation email — whichever's easier.</p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</p>
      <p className="font-serif text-xl text-white">{value}</p>
    </div>
  );
}